/**
 * Why ZenZest — full-width neighborhoods marquee + four reason cards +
 * Why-ZenZest internal-link CTA block. Pure presentation.
 */
import { Link } from "react-router-dom";
import { Leaf, Users, Home as HomeIcon, ShieldCheck } from "lucide-react";

function Why({ icon, title, body }) {
    return (
        <div className="bg-zen-surface border border-zen-ink/8 p-8 sm:p-10 hover:border-zen-olive/50 hover:bg-zen-bg transition-all duration-500 group">
            <div className="mb-8 transition-transform duration-500 group-hover:scale-110 inline-block">
                {icon}
            </div>
            <h3 className="font-serif text-2xl text-zen-ink font-light leading-tight mb-3">
                {title}
            </h3>
            <p className="text-sm text-zen-ink-soft leading-relaxed">{body}</p>
        </div>
    );
}

export default function HomeWhy({ cities }) {
    return (
        <>
            {/* Neighborhoods marquee */}
            <section className="border-y border-zen-ink/10 bg-zen-surface py-16 overflow-hidden">
                <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive text-center mb-8">
                    Serving 80+ neighborhoods across NY & NJ
                </p>
                <div className="flex zen-marquee whitespace-nowrap will-change-transform">
                    {[...cities.slice(0, 40), ...cities.slice(0, 40)].map((c, i) => (
                        <Link
                            key={`${c.slug}-${i}`}
                            to={`/dispensary-near/${c.slug}`}
                            className="inline-flex items-center mx-6 text-2xl md:text-3xl font-serif font-light text-zen-ink-soft hover:text-zen-olive transition-colors"
                        >
                            {c.city}
                            <span className="mx-6 text-zen-olive/60">·</span>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Why cards */}
            <section className="relative py-12 sm:py-16 px-6 sm:px-10 bg-zen-bg">
                <div className="max-w-[1400px] mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
                        <div className="lg:col-span-5">
                            <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive mb-5">
                                Why ZenZest
                            </p>
                            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zen-ink leading-[1.05]">
                                Four reasons people keep coming back.
                            </h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 self-end">
                            <p className="text-zen-ink-soft leading-relaxed text-base">
                                We didn&rsquo;t open a dispensary to chase a
                                trend — we opened it because we love this plant
                                and we wanted New York to experience cannabis
                                the way it deserves to be experienced.
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        <Why
                            icon={<Leaf size={28} className="text-zen-olive" />}
                            title="Hand-curated selection"
                            body="Every brand on our shelves is personally vetted. If it isn't excellent, it doesn't make the cut."
                        />
                        <Why
                            icon={<Users size={28} className="text-zen-olive-dark" />}
                            title="People who genuinely care"
                            body="Trained, certified budtenders obsessed with helping you find your perfect product."
                        />
                        <Why
                            icon={<HomeIcon size={28} className="text-zen-olive" />}
                            title="Family-owned, NY-rooted"
                            body="Not a chain. Built by New Yorkers — serving Queens, Nassau LI, and Staten Island first."
                        />
                        <Why
                            icon={<ShieldCheck size={28} className="text-zen-olive-dark" />}
                            title="Lab-tested, always"
                            body="Every product third-party tested for purity, potency, and pesticide-free quality."
                        />
                    </div>
                </div>
            </section>

            {/* Why-ZenZest CTA block */}
            <section
                data-testid="home-why-block"
                className="border-t border-zen-ink/8 bg-zen-bg"
            >
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12 sm:py-14">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive mb-3">
                                The ZenZest difference
                            </p>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-zen-ink leading-[1.05] max-w-3xl">
                                Why shoppers pick us.
                            </h2>
                        </div>
                        <Link
                            to="/why-zenzest"
                            data-testid="home-why-link"
                            className="text-[12px] uppercase tracking-[0.25em] text-zen-ink-soft hover:text-zen-ink inline-flex items-center gap-2 self-start md:self-end"
                        >
                            See the full breakdown →
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
